import { MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { ComponentProps, FC, ReactNode } from 'react';
import { LoadableSelect } from '../../form/LoadableSelect';
import { useActionStatusOptions } from './useActionStatusOptions';
import { Add, Circle } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';

type StatusSelectProps = Omit<ComponentProps<typeof Select<string>>, 'placeholder'> &
	Readonly<{
		loading?: boolean;
		placeholder?: ReactNode | string;
	}>;

export const StatusSelect: FC<StatusSelectProps> = ({ value, loading, placeholder, ...props }) => {
	const theme = useTheme();
	const { statusOptions } = useActionStatusOptions();

	return (
		<LoadableSelect
			variant={'standard'}
			loading={loading}
			value={value || '_placeholder'}
			renderValue={(value) => {
				if (loading) {
					return (
						<Typography>
							<FormattedMessage id={'common.loading'} />
						</Typography>
					);
				}

				const option = statusOptions.find((o) => (o.value as string) === value);

				if (option) {
					return <StatusItem {...option} />;
				}

				if (placeholder != null) {
					return placeholder;
				}

				return (
					<Stack direction={'row'} gap={1} alignItems={'center'} sx={{ color: theme.palette.secondary.main }}>
						<Add sx={{ fontSize: '18px' }} />
						<Typography fontSize={'14px'}>
							<FormattedMessage id={'action.detail.status.placeholder'} />
						</Typography>
					</Stack>
				);
			}}
			sx={{
				'&::before': {
					content: 'none',
				},
				minHeight: '33px',
			}}
			IconComponent={() => null}
			{...props}
		>
			{statusOptions.map((option) => (
				<MenuItem key={option.value} value={option.value}>
					<StatusItem {...option} />
				</MenuItem>
			))}
		</LoadableSelect>
	);
};

export const StatusItem: FC<ReturnType<typeof useActionStatusOptions>['statusOptions'][number]> = ({ text, color }) => (
	<Stack direction={'row'} gap={1.25} alignItems={'center'}>
		<Circle style={{ fontSize: '9.6px', color: color }} />
		<Typography fontSize={'14px'}>{text}</Typography>
	</Stack>
);
